import React from "react";
import {
  headingSizeClassName,
  textSizeClassName,
  textToneClassName,
  type HeadingSize,
  type TextSize,
  type TextTone,
} from "@toolkit/design";
import { cn } from "../../lib/utils";

type KickerProps = React.HTMLAttributes<HTMLParagraphElement> & {
  tone?: "primary" | "accent";
};

export const Kicker: React.FC<KickerProps> = ({
  className,
  tone = "primary",
  ...props
}) => (
  <p
    className={cn(
      tone === "accent" ? "ds-kicker-accent" : "ds-kicker",
      className,
    )}
    {...props}
  />
);

type HeadingLevel = 1 | 2 | 3 | 4;

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  level?: HeadingLevel;
  size?: HeadingSize;
};

const HEADING_TAG = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
} as const satisfies Record<HeadingLevel, keyof React.JSX.IntrinsicElements>;

export const Heading: React.FC<HeadingProps> = ({
  level = 2,
  size = "lg",
  className,
  ...props
}) => {
  const Component = HEADING_TAG[level];
  return (
    <Component
      className={cn(
        "font-display text-foreground",
        headingSizeClassName[size],
        className,
      )}
      {...props}
    />
  );
};

type TextProps = React.HTMLAttributes<HTMLParagraphElement> & {
  tone?: TextTone;
  size?: TextSize;
};

export const Text: React.FC<TextProps> = ({
  tone = "default",
  size = "md",
  className,
  ...props
}) => (
  <p
    className={cn(
      "leading-relaxed",
      textSizeClassName[size],
      textToneClassName[tone],
      className,
    )}
    {...props}
  />
);
